/**
 * useChangePolicy — read/write the caller's change-request auto-policy.
 *
 * Phase G.7.6 (sacred/change-doctrine.md §VII):
 *   Each Orchist may set a global default + per-Orchist overrides for how
 *   incoming change-requests to their record are handled.
 *
 * Policy values:
 *   - 'manual' (default-of-defaults; must respond explicitly)
 *   - 'auto_accept'
 *   - 'auto_reject' (per-Orchist only — never as global default)
 *   - 'auto_ignore' (let the cosmos accept after 30 days)
 *
 * RPCs:
 *   - core.set_change_request_default(p_policy)
 *   - core.set_change_request_override(p_target_contact_id, p_policy | 'clear')
 *
 * The policy itself is stored on Contact.changeRequestPolicy as a JSONB:
 *   { default: 'manual' | 'auto_accept' | 'auto_ignore',
 *     perOrchist: { '<contactId>': '<policy>' } }
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type DefaultPolicy = 'manual' | 'auto_accept' | 'auto_ignore';
export type OverridePolicy = 'manual' | 'auto_accept' | 'auto_reject' | 'auto_ignore';

export interface ChangePolicyShape {
  default?: DefaultPolicy;
  perOrchist?: Record<string, OverridePolicy>;
}

interface UseChangePolicyState {
  loading: boolean;
  policy: ChangePolicyShape;
  error: Error | null;
}

export function useChangePolicy() {
  const { contact } = useAuth();
  const [state, setState] = useState<UseChangePolicyState>({
    loading: true,
    policy: {},
    error: null,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setState({ loading: false, policy: {}, error: null });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('Contact')
        .select('changeRequestPolicy')
        .eq('id', contact.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setState({ loading: false, policy: {}, error: new Error(error.message) });
        return;
      }
      const raw = (data?.changeRequestPolicy ?? {}) as ChangePolicyShape;
      setState({ loading: false, policy: raw, error: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  const setDefault = useCallback(async (policy: DefaultPolicy) => {
    const { error } = await supabase
      .schema('core')
      .rpc('set_change_request_default', { p_policy: policy });
    if (error) throw new Error(error.message);
    refresh();
  }, [refresh]);

  const setOverride = useCallback(
    async (targetContactId: string, policy: OverridePolicy | 'clear') => {
      const { error } = await supabase
        .schema('core')
        .rpc('set_change_request_override', {
          p_target_contact_id: targetContactId,
          p_policy: policy,
        });
      if (error) throw new Error(error.message);
      refresh();
    },
    [refresh]
  );

  return { ...state, setDefault, setOverride, refresh };
}

/** Resolve effective policy for a specific other Orchist. */
export function effectivePolicy(
  shape: ChangePolicyShape,
  otherContactId: string
): OverridePolicy {
  const override = shape.perOrchist?.[otherContactId];
  if (override) return override;
  return shape.default ?? 'manual';
}
